import React, { useEffect, useRef, useState, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { Plus, Clock, ChevronRight, ChevronDown, Users, AlertTriangle, Search, X, Minus, LogOut, CheckSquare, Check, Trash2 } from 'lucide-react';
import { useTab } from 'context/restaurant/TabContext';
import { useUser } from 'context/user/User';
import BottomSheet from 'components/_UI/BottomSheet';
import commandsApi from 'api/requests/commands';
import stationOrdersApi from 'api/requests/stationOrders';
import eventsApi from 'api/requests/events';

import paymentMethodsApi from 'api/requests/paymentMethods';
import colors from 'theme/colors';
import pusherService from 'services/pusher';

function timeAgo(dateStr) {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Agora';
    if (mins < 60) return `${mins}min`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ${mins % 60}min`;
    return `${Math.floor(hrs / 24)}d`;
}

function formatPrice(value) {
    return Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export default function TabSelectionScreen() {
    const { setActiveTab } = useTab();
    const { user } = useUser();
    const slug = user?.establishments?.[0]?.slug;
    const establishmentId = user?.establishments?.[0]?.id;

    const [activeView, setActiveView] = useState('open'); // 'open' | 'closed'

    const [tabs, setTabs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [closedTabs, setClosedTabs] = useState([]);
    const [closedLoading, setClosedLoading] = useState(false);
    const [newTabSheet, setNewTabSheet] = useState(false);
    const [personName, setPersonName] = useState('');
    const [tableIdentifier, setTableIdentifier] = useState('');
    const [creating, setCreating] = useState(false);
    const [newTabStep, setNewTabStep] = useState(1);
    const [activeEvents, setActiveEvents] = useState([]);
    const [eventsLoading, setEventsLoading] = useState(false);
    const [ticketSelections, setTicketSelections] = useState([]); // [{ event, batch, quantity, totalPrice }]

    const [createTicketError, setCreateTicketError] = useState(null);

    const [detailTab, setDetailTab] = useState(null);
    const [detailData, setDetailData] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [paymentStep, setPaymentStep] = useState(false);
    const [closing, setClosing] = useState(false);

    const [paymentMethods, setPaymentMethods] = useState([]);
    const [paymentMethodsLoading, setPaymentMethodsLoading] = useState(false);
    const [customerCount, setCustomerCount] = useState(1);
    const [selectedMethods, setSelectedMethods] = useState([null]);

    const [tipEnabled, setTipEnabled] = useState(true);
    const [splitExpanded, setSplitExpanded] = useState(false);

    const [confirmAction, setConfirmAction] = useState(null); // { tab, type: 'close' | 'cancel' }
    const [cancelReason, setCancelReason] = useState('');

    const [period, setPeriod] = useState('today'); // null | 'today' | 'week' | 'month'

    const [deleteItemTarget, setDeleteItemTarget] = useState(null); // { id, product_name }
    const [deletingItem, setDeletingItem] = useState(false);

    const [searchOpen, setSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const searchInputRef = useRef(null);

    const [selectMode, setSelectMode] = useState(false);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [batchSheet, setBatchSheet] = useState(false);
    const [batchClosing, setBatchClosing] = useState(false);
    const [batchProgress, setBatchProgress] = useState({ done: 0, total: 0, errors: [] });
    const [batchPaymentMethod, setBatchPaymentMethod] = useState(null);
    const [batchTipEnabled, setBatchTipEnabled] = useState(true);

    const [batchDetails, setBatchDetails] = useState({});
    const [batchDetailsLoading, setBatchDetailsLoading] = useState(false);
    const [batchExpandedIds, setBatchExpandedIds] = useState(new Set());
    const [readyCommandIds, setReadyCommandIds] = useState(new Set());
    const [ordersSheet, setOrdersSheet] = useState({ open: false, tab: null, data: null, loading: false });

    const selectedTabs = tabs.filter(t => selectedIds.has(t.id));
    const selectedTotal = selectedTabs.reduce((sum, t) => sum + Number(t.total || 0), 0);

    const [logoutSheet, setLogoutSheet] = useState(false);

    const otpUser = (() => {
        try {
            const raw = localStorage.getItem('otp_user');
            return raw ? JSON.parse(raw) : null;
        } catch { return null; }
    })();
    const otpUserName = otpUser?.name;

    const isWaiterRoute = /^\/[^/]+\/(garcom|estacao)/.test(window.location.pathname);
    const isWaiterSession = isWaiterRoute && otpUser?.role == import.meta.env.VITE_WAITER_ROLE_ID;
    const isEstablishmentSession = !isWaiterRoute;
    const otpUserEstablishment = otpUser?.establishments?.[0]?.name;

    function handleLogout() {
        localStorage.removeItem('otp_token');
        localStorage.removeItem('otp_user');
        window.location.href = '/acesso';
    }

    const q = searchQuery.trim().toLowerCase();
    const filteredTabs = q
        ? tabs.filter(t =>
            t.table_identifier?.toLowerCase().includes(q) ||
            t.person_name?.toLowerCase().includes(q)
        )
        : tabs;

    function handleToggleSearch() {
        if (searchOpen) {
            setSearchQuery('');
            setSearchOpen(false);
        } else {
            setSearchOpen(true);
            setTimeout(() => searchInputRef.current?.focus(), 50);
        }
    }

    function handleClearSearch() {
        if (searchQuery) {
            setSearchQuery('');
            searchInputRef.current?.focus();
        } else {
            setSearchOpen(false);
        }
    }

    function handleSwitchView(view) {
        if (view === activeView) return;
        setActiveView(view);
        if (selectMode) {
            setSelectMode(false);
            setSelectedIds(new Set());
        }
    }

    function handleToggleSelectMode() {
        if (selectMode) {
            setSelectedIds(new Set());
        }
        setSelectMode(prev => !prev);
    }

    function handleToggleSelect(tabId) {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(tabId)) next.delete(tabId);
            else next.add(tabId);
            return next;
        });
    }

    function handleOpenBatchClose() {
        setBatchSheet(true);
        setBatchPaymentMethod(null);
        setBatchTipEnabled(true);
        setBatchProgress({ done: 0, total: 0, errors: [] });
        setBatchDetails({});
        setBatchDetailsLoading(true);
        const ids = [...selectedIds];
        setBatchExpandedIds(new Set(ids.length > 0 ? [ids[0]] : []));
        setPaymentMethodsLoading(true);
        paymentMethodsApi.list()
            .then(({ data }) => setPaymentMethods(data.data || data || []))
            .catch(() => { })
            .finally(() => setPaymentMethodsLoading(false));
        Promise.allSettled(ids.map(id => commandsApi.get(slug, id)))
            .then(results => {
                const details = {};
                results.forEach((result, i) => {
                    if (result.status === 'fulfilled') {
                        details[ids[i]] = result.value.data;
                    }
                });
                setBatchDetails(details);
            })
            .finally(() => setBatchDetailsLoading(false));
    }

    function handleToggleBatchExpand(tabId) {
        setBatchExpandedIds(prev => {
            const next = new Set(prev);
            if (next.has(tabId)) next.delete(tabId);
            else next.add(tabId);
            return next;
        });
    }

    async function handleBatchCloseConfirm() {
        if (!batchPaymentMethod) return;
        const ids = [...selectedIds];
        setBatchClosing(true);
        setBatchProgress({ done: 0, total: ids.length, errors: [] });

        const errors = [];
        for (let i = 0; i < ids.length; i++) {
            try {
                const payload = { payment_method_ids: [batchPaymentMethod] };
                if (batchTipEnabled) payload.tip_percentage = 10;
                console.log('API payload batch info:', {
                    tabId: ids[i],
                    tipEnabled: batchTipEnabled,
                    payload
                });
                await commandsApi.close(slug, ids[i], payload);
                setTabs(prev => prev.filter(t => t.id !== ids[i]));
                setSelectedIds(prev => {
                    const next = new Set(prev);
                    next.delete(ids[i]);
                    return next;
                });
            } catch {
                const failedTab = tabs.find(t => t.id === ids[i]);
                errors.push(failedTab?.table_identifier || `#${ids[i]}`);
            }
            setBatchProgress({ done: i + 1, total: ids.length, errors: [...errors] });
        }

        setBatchClosing(false);
        if (errors.length === 0) {
            setBatchSheet(false);
            setSelectMode(false);
            setSelectedIds(new Set());
        }
    }

    const loadTabs = useCallback(() => {
        if (!slug) return;
        setLoading(true);
        const params = { status: 'open' };
        if (period) params.period = period;
        commandsApi.list(slug, params)
            .then(({ data }) => setTabs(data.data || []))
            .catch(() => { })
            .finally(() => setLoading(false));
    }, [slug, period]);

    // Initial load + auto-refresh polling every 30s (H)
    useEffect(() => {
        loadTabs();
        const interval = setInterval(() => {
            if (!slug) return;
            const params = { status: 'open' };
            if (period) params.period = period;
            commandsApi.list(slug, params)
                .then(({ data }) => setTabs(data.data || []))
                .catch(() => { });
        }, 30000);
        return () => clearInterval(interval);
    }, [loadTabs, slug]);

    const loadClosedTabs = useCallback(() => {
        if (!slug) return;
        setClosedLoading(true);
        const params = { status: 'closed' };
        if (period) params.period = period;
        commandsApi.list(slug, params)
            .then(({ data }) => setClosedTabs(data.data || []))
            .catch(() => { })
            .finally(() => setClosedLoading(false));
    }, [slug, period]);

    useEffect(() => {
        if (activeView === 'closed') loadClosedTabs();
    }, [activeView, loadClosedTabs]);

    const filteredClosedTabs = q
        ? closedTabs.filter(t =>
            t.table_identifier?.toLowerCase().includes(q) ||
            t.person_name?.toLowerCase().includes(q)
        )
        : closedTabs;

    // Refresh time display every minute
    const [, setTick] = useState(0);
    useEffect(() => {
        const interval = setInterval(() => setTick(t => t + 1), 60000);
        return () => clearInterval(interval);
    }, []);

    // Pusher: listen for station order status updates to drive ready dot
    useEffect(() => {
        if (!establishmentId) return;
        const channelName = `establishments.${establishmentId}`;

        const handleStationOrderUpdated = (data) => {
            if (data.status === 'ready' && data.command_id) {
                setReadyCommandIds(prev => new Set([...prev, data.command_id]));
            }
        };

        pusherService.subscribe(channelName, 'station_order.status_updated', handleStationOrderUpdated);

        return () => {
            pusherService.unsubscribe(channelName, 'station_order.status_updated', handleStationOrderUpdated);
        };
    }, [establishmentId]);

    async function handleCreateTab() {
        if (!personName.trim() || !tableIdentifier.trim()) return;
        setCreating(true);
        try {
            const { data: newTab } = await commandsApi.create(slug, {
                person_name: personName.trim(),
                table_identifier: tableIdentifier.trim(),
            });
            setTabs(prev => [newTab, ...prev]);
            setActiveTab(newTab);
            setNewTabSheet(false);
            setPersonName('');
            setTableIdentifier('');
            setNewTabStep(1);
            setTicketSelections([]);
            return newTab;
        } catch {
            return null;
        } finally {
            setCreating(false);
        }
    }

    async function handleCreateWithTickets() {
        const newTab = await handleCreateTab();
        if (!newTab || ticketSelections.length === 0) return;

        try {
            const items = ticketSelections.map(sel => ({
                type: 'ticket',
                event_id: sel.event.id,
                batch_id: sel.batch.id,
                quantity: sel.quantity,
            }));
            await commandsApi.addItemsBatch(slug, newTab.id, { items });
        } catch (err) {
            const msg = err?.response?.data?.message || '';
            const isSoldOut = msg.toLowerCase().includes('sold out') || msg.toLowerCase().includes('esgotado');
            setCreateTicketError(
                isSoldOut
                    ? 'Ingresso esgotado. A comanda foi aberta normalmente.'
                    : 'Ingresso indisponível. A comanda foi aberta normalmente.'
            );
            setTimeout(() => setCreateTicketError(null), 5000);
        }
    }

    function handleSetTicketQty(event, batch, newQty) {
        if (newQty <= 0) {
            setTicketSelections(prev => prev.filter(s => s.batch.id !== batch.id));
            return;
        }
        const qty = Math.min(newQty, batch.available ?? 99);
        setTicketSelections(prev => {
            const existing = prev.findIndex(s => s.batch.id === batch.id);
            const entry = { event, batch, quantity: qty, totalPrice: Number(batch.price) * qty };
            if (existing >= 0) {
                const next = [...prev];
                next[existing] = entry;
                return next;
            }
            return [...prev, entry];
        });
    }

    async function handleOpenDetail(tab, e) {
        e.stopPropagation();
        setDetailTab(tab);
        setDetailData(null);
        setDetailLoading(true);
        setPaymentStep(false);
        setCustomerCount(1);
        setSelectedMethods([null]);
        try {
            const { data } = await commandsApi.get(slug, tab.id);
            setDetailData(data);
        } catch { }
        finally {
            setDetailLoading(false);
        }
    }

    function handleCloseDetail() {
        setDetailTab(null);
        setDetailData(null);
        setPaymentStep(false);
        setCustomerCount(1);
        setSelectedMethods([null]);
        setTipEnabled(true);
    }

    async function handleDeleteItem() {
        if (!deleteItemTarget || !detailTab) return;
        setDeletingItem(true);
        try {
            await commandsApi.deleteItem(slug, detailTab.id, deleteItemTarget.id);
            setDetailData(prev => {
                if (!prev) return prev;
                const updatedItems = prev.items.filter(i => i.id !== deleteItemTarget.id);
                const newTotal = updatedItems.reduce((sum, i) => sum + Number(i.total_price || 0), 0);
                return { ...prev, items: updatedItems, total: newTotal };
            });
            setTabs(prev => prev.map(t => {
                if (t.id !== detailTab.id) return t;
                const updatedItems = (detailData?.items || []).filter(i => i.id !== deleteItemTarget.id);
                const newTotal = updatedItems.reduce((sum, i) => sum + Number(i.total_price || 0), 0);
                return { ...t, total: newTotal };
            }));
        } catch (err) {
            const msg = err?.response?.data?.message || 'Erro ao remover item.';
            alert(msg);
        }
        setDeleteItemTarget(null);
        setDeletingItem(false);
    }

    function handleOpenPaymentStep() {
        if (Number(detailData?.total || 0) === 0) {
            handleCloseFromDetail();
            return;
        }
        setPaymentStep(true);
        setSplitExpanded(false);
        setPaymentMethodsLoading(true);
        paymentMethodsApi.list()
            .then(({ data }) => setPaymentMethods(data.data || data || []))
            .catch(() => { })
            .finally(() => setPaymentMethodsLoading(false));
    }

    async function handleOpenClose(tab, e) {
        e.stopPropagation();
        setDetailTab(tab);
        setDetailData(null);
        setDetailLoading(true);
        setPaymentStep(false);
        setSplitExpanded(false);
        setCustomerCount(1);
        setSelectedMethods([null]);
        setPaymentMethodsLoading(true);
        paymentMethodsApi.list()
            .then(({ data }) => setPaymentMethods(data.data || data || []))
            .catch(() => { })
            .finally(() => setPaymentMethodsLoading(false));
        try {
            const { data } = await commandsApi.get(slug, tab.id);
            setDetailData(data);
        } catch { }
        finally {
            setDetailLoading(false);
        }
    }

    function handleChangeCustomerCount(delta) {
        setCustomerCount(prev => {
            const next = Math.max(1, Math.min(20, prev + delta));
            setSelectedMethods(curr => {
                if (next > curr.length) {
                    return [...curr, ...Array(next - curr.length).fill(null)];
                }
                return curr.slice(0, next);
            });
            return next;
        });
    }

    function handleSelectMethod(customerIndex, methodId) {
        setSelectedMethods(prev =>
            prev.map((m, i) => i === customerIndex ? methodId : m)
        );
    }

    async function handleCloseFromDetail() {
        if (!detailTab) return;
        setClosing(true);
        try {
            const total = Number(detailData.total || 0);
            const payload = {};
            if (total > 0) {
                const totalAmount = tipEnabled ? total * 1.1 : total;
                payload.payments = selectedMethods.map(id => ({
                    payment_method_id: id,
                    amount: parseFloat((totalAmount / customerCount).toFixed(2)),
                }));
            }
            if (tipEnabled && total > 0) payload.tip_percentage = 10;
            console.log('API payload detail info:', {
                tabId: detailTab.id,
                tipEnabled,
                payload
            });
            await commandsApi.close(slug, detailTab.id, payload);
            setTabs(prev => prev.filter(t => t.id !== detailTab.id));
            handleCloseDetail();
        } catch { }
        finally {
            setClosing(false);
        }
    }

    async function handleConfirmedAction() {
        if (!confirmAction) return;
        const { tab, type } = confirmAction;
        try {
            if (type === 'close') {
                await commandsApi.close(slug, tab.id);
            } else {
                const payload = {};
                if (cancelReason.trim()) payload.cancel_reason = cancelReason.trim();
                await commandsApi.cancel(slug, tab.id, payload);
            }
            setTabs(prev => prev.filter(t => t.id !== tab.id));
        } catch { }
        setConfirmAction(null);
        setCancelReason('');
    }

    async function handleOpenOrdersSheet(tab, e) {
        e.stopPropagation();
        setReadyCommandIds(prev => {
            const next = new Set(prev);
            next.delete(tab.id);
            return next;
        });
        setOrdersSheet({ open: true, tab, data: null, loading: true });
        try {
            const { data } = await stationOrdersApi.list(slug, { command_id: tab.id });
            setOrdersSheet({ open: true, tab, data: data.data || data, loading: false });
        } catch {
            setOrdersSheet({ open: true, tab, data: null, loading: false });
        }
    }

    async function handleRefreshOrdersSheet() {
        if (!ordersSheet.tab) return;
        setOrdersSheet(prev => ({ ...prev, loading: true }));
        try {
            const { data } = await stationOrdersApi.list(slug, { command_id: ordersSheet.tab.id });
            setOrdersSheet(prev => ({ ...prev, data: data.data || data, loading: false }));
        } catch {
            setOrdersSheet(prev => ({ ...prev, loading: false }));
        }
    }

    function openNewTabSheet() {
        setPersonName('');
        setTableIdentifier('');
        setNewTabStep(1);
        setActiveEvents([]);
        setTicketSelections([]);
        setNewTabSheet(true);
    }

    async function handleNextStep() {
        if (!canSubmit) return;
        setEventsLoading(true);
        try {
            const { data } = await eventsApi.getActive(slug);
            const events = data || [];
            if (events.length === 0) {
                await handleCreateTab();
            } else {
                setActiveEvents(events);
                setNewTabStep(2);
            }
        } catch {
            await handleCreateTab();
        } finally {
            setEventsLoading(false);
        }
    }

    const canSubmit = personName.trim() && tableIdentifier.trim();

    const ACTIVE_ORDER_STATUSES = ['pending', 'preparing', 'ready'];
    const ORDER_STATUS_LABELS = { pending: 'Pendente', preparing: 'Preparando', ready: 'Pronto' };
    const ORDER_STATUS_COLORS = {
        pending: colors.occasionalOrange,
        preparing: colors.occasionalBlue,
        ready: colors.occasionalGreen,
    };

    return (
        <Screen>
            {/* Header */}
            <PageHeader>
                <HeaderRow>
                    <HeaderLeft>
                        {isWaiterSession && (
                            <LogoutBtn onClick={() => setLogoutSheet(true)} aria-label="Encerrar turno">
                                <LogOut size={18} strokeWidth={2.5} />
                            </LogoutBtn>
                        )}
                        <HeaderTitleGroup>
                            <HeaderTitle>Comandas</HeaderTitle>
                            <HeaderCount>
                                {activeView === 'closed'
                                    ? q
                                        ? `${filteredClosedTabs.length} de ${closedTabs.length} fechada${closedTabs.length !== 1 ? 's' : ''}`
                                        : `${closedTabs.length} fechada${closedTabs.length !== 1 ? 's' : ''}`
                                    : selectMode
                                        ? `${selectedIds.size} selecionada${selectedIds.size !== 1 ? 's' : ''}`
                                        : q
                                            ? `${filteredTabs.length} de ${tabs.length} aberta${tabs.length !== 1 ? 's' : ''}`
                                            : `${tabs.length} aberta${tabs.length !== 1 ? 's' : ''}`
                                }
                            </HeaderCount>
                            {isWaiterSession && otpUserName && (
                                <WaiterIdentity>
                                    {otpUserName}
                                    {otpUserEstablishment && <> · {otpUserEstablishment}</>}
                                </WaiterIdentity>
                            )}
                        </HeaderTitleGroup>
                    </HeaderLeft>
                    <HeaderRight>
                        {activeView === 'open' && tabs.length > 0 && (
                            <SelectModeBtn onClick={handleToggleSelectMode} $active={selectMode} aria-label="Modo seleção">
                                <CheckSquare size={18} strokeWidth={2.5} />
                            </SelectModeBtn>
                        )}
                        {activeView === 'open' && (
                            <NewTabBtn onClick={openNewTabSheet} aria-label="Nova comanda">
                                <Plus size={20} color="#fff" strokeWidth={2.5} />
                            </NewTabBtn>
                        )}
                    </HeaderRight>
                </HeaderRow>

                <AlwaysVisibleSearchBar>
                    <SearchIconWrap>
                        <Search size={16} color={colors.nightLight} />
                    </SearchIconWrap>
                    <AlwaysVisibleSearchInput
                        ref={searchInputRef}
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        placeholder="Buscar por mesa, nome..."
                    />
                    {searchQuery && (
                        <ClearInputBtn onClick={handleClearSearch} $visible={true} type="button">
                            <X size={11} color="#fff" strokeWidth={3} />
                        </ClearInputBtn>
                    )}
                </AlwaysVisibleSearchBar>

                <ViewTabs>
                    <ViewTab $active={activeView === 'open'} onClick={() => handleSwitchView('open')}>
                        Abertas{tabs.length > 0 ? ` (${tabs.length})` : ''}
                    </ViewTab>
                    <ViewTab $active={activeView === 'closed'} onClick={() => handleSwitchView('closed')}>
                        Fechadas{closedTabs.length > 0 ? ` (${closedTabs.length})` : ''}
                    </ViewTab>
                </ViewTabs>

                <PeriodFilter>
                    {[
                        { value: null,    label: 'Todos' },
                        { value: 'today', label: 'Hoje' },
                        { value: 'week',  label: 'Semana' },
                        { value: 'month', label: 'Mês' },
                    ].map(({ value, label }) => (
                        <PeriodChip
                            key={label}
                            $active={period === value}
                            onClick={() => setPeriod(value)}
                        >
                            {label}
                        </PeriodChip>
                    ))}
                </PeriodFilter>
            </PageHeader>

            {/* Tab List */}
            <TabList>
                {createTicketError && (
                    <TicketErrorBanner>
                        {createTicketError}
                    </TicketErrorBanner>
                )}
                {activeView === 'open' && (
                    <>
                        {loading && (
                            <LoadingWrap>
                                <Spinner />
                                <LoadingText>Carregando comandas...</LoadingText>
                            </LoadingWrap>
                        )}

                        {!loading && tabs.length === 0 && (
                            <EmptyState>
                                <EmptyIconWrap>
                                    <Users size={32} color={colors.nightLight} />
                                </EmptyIconWrap>
                                <EmptyTitle>Nenhuma comanda aberta</EmptyTitle>
                                <EmptyHint>Toque no botão + para abrir uma nova comanda e começar a registrar pedidos</EmptyHint>
                            </EmptyState>
                        )}

                        {!loading && tabs.length > 0 && filteredTabs.length === 0 && (
                            <EmptyState>
                                <EmptyIconWrap>
                                    <Search size={32} color={colors.nightLight} />
                                </EmptyIconWrap>
                                <EmptyTitle>Nenhum resultado</EmptyTitle>
                                <EmptyHint>Nenhuma comanda encontrada para "{searchQuery}"</EmptyHint>
                            </EmptyState>
                        )}

                        {filteredTabs.map((tab, index) => (
                            <TabCard
                                key={tab.id}
                                $index={index}
                                $selected={selectMode && selectedIds.has(tab.id)}
                                $ready={readyCommandIds.has(tab.id)}
                                onClick={() => selectMode ? handleToggleSelect(tab.id) : setActiveTab(tab)}
                            >
                                {readyCommandIds.has(tab.id) && (
                                    <ReadyBanner>
                                        <ReadyDotWrap>
                                            <ReadyDotRing />
                                            <ReadyDotInner />
                                        </ReadyDotWrap>
                                        Pedido pronto para retirada
                                    </ReadyBanner>
                                )}
                                <TabCardMain>
                                    {selectMode ? (
                                        <SelectCheckbox $checked={selectedIds.has(tab.id)}>
                                            {selectedIds.has(tab.id) && <Check size={14} color="#fff" strokeWidth={3} />}
                                        </SelectCheckbox>
                                    ) : (
                                        <TabStatusCol>
                                            <StatusDot />
                                            {tab.created_at && (
                                                <TimeTag>
                                                    <Clock size={10} color={colors.night} />
                                                    {timeAgo(tab.created_at)}
                                                </TimeTag>
                                            )}
                                        </TabStatusCol>
                                    )}
                                    <TabInfo>
                                        <TabIdentifier>{tab.table_identifier}</TabIdentifier>
                                        <TabPerson>{tab.person_name}</TabPerson>
                                        {tab.created_by?.name && (
                                            <TabWaiter>🧑‍🍳 Garçom: {tab.created_by.name}</TabWaiter>
                                        )}
                                    </TabInfo>
                                    <TabRight>
                                        <TabTotal>{formatPrice(tab.total)}</TabTotal>
                                        {!selectMode && (
                                            <TabArrow>
                                                <ChevronRight size={18} color={colors.nightLight} />
                                            </TabArrow>
                                        )}
                                    </TabRight>
                                </TabCardMain>
                                {!selectMode && (
                                    <TabActions>
                                        <TabActionBtn onClick={(e) => handleOpenDetail(tab, e)}>
                                            Detalhes
                                        </TabActionBtn>
                                        <TabActionDivider />
                                        <TabActionBtn $variant="orders" onClick={(e) => handleOpenOrdersSheet(tab, e)}>
                                            Pedidos em andamento
                                            {readyCommandIds.has(tab.id) && (
                                                <ReadyDotWrap>
                                                    <ReadyDotRing />
                                                    <ReadyDotInner />
                                                </ReadyDotWrap>
                                            )}
                                        </TabActionBtn>
                                        {isEstablishmentSession && (
                                            <>
                                                <TabActionDivider />
                                                <TabActionBtn
                                                    $variant="close"
                                                    onClick={(e) => handleOpenClose(tab, e)}
                                                >
                                                    Fechar
                                                </TabActionBtn>
                                                <TabActionDivider />
                                                <TabActionBtn
                                                    $variant="cancel"
                                                    onClick={(e) => { e.stopPropagation(); setConfirmAction({ tab, type: 'cancel' }); }}
                                                >
                                                    Cancelar
                                                </TabActionBtn>
                                            </>
                                        )}
                                    </TabActions>
                                )}
                            </TabCard>
                        ))}

                        {!loading && <BottomSpacer $extraSpace={selectMode && selectedIds.size > 0} />}
                    </>
                )}

                {activeView === 'closed' && (
                    <>
                        {closedLoading && (
                            <LoadingWrap>
                                <Spinner />
                                <LoadingText>Carregando comandas fechadas...</LoadingText>
                            </LoadingWrap>
                        )}

                        {!closedLoading && closedTabs.length === 0 && (
                            <EmptyState>
                                <EmptyIconWrap>
                                    <Check size={32} color={colors.nightLight} />
                                </EmptyIconWrap>
                                <EmptyTitle>Nenhuma comanda fechada</EmptyTitle>
                                <EmptyHint>Comandas fechadas aparecerão aqui</EmptyHint>
                            </EmptyState>
                        )}

                        {!closedLoading && closedTabs.length > 0 && filteredClosedTabs.length === 0 && (
                            <EmptyState>
                                <EmptyIconWrap>
                                    <Search size={32} color={colors.nightLight} />
                                </EmptyIconWrap>
                                <EmptyTitle>Nenhum resultado</EmptyTitle>
                                <EmptyHint>Nenhuma comanda encontrada para "{searchQuery}"</EmptyHint>
                            </EmptyState>
                        )}

                        {filteredClosedTabs.map((tab, index) => (
                            <TabCard
                                key={tab.id}
                                $index={index}
                                onClick={(e) => handleOpenDetail(tab, e)}
                            >
                                <TabCardMain>
                                    <TabStatusCol>
                                        <ClosedDot />
                                        {tab.closed_at && (
                                            <TimeTag>
                                                <Clock size={10} color={colors.night} />
                                                {timeAgo(tab.closed_at)}
                                            </TimeTag>
                                        )}
                                    </TabStatusCol>
                                    <TabInfo>
                                        <TabIdentifier>{tab.table_identifier}</TabIdentifier>
                                        <TabPerson>{tab.person_name}</TabPerson>
                                    </TabInfo>
                                    <TabRight>
                                        <TabTotal>{formatPrice(tab.total)}</TabTotal>
                                        {tab.tip_value > 0 && (
                                            <TipBadge>+10%</TipBadge>
                                        )}
                                        <TabArrow>
                                            <ChevronRight size={18} color={colors.nightLight} />
                                        </TabArrow>
                                    </TabRight>
                                </TabCardMain>
                            </TabCard>
                        ))}

                        {!closedLoading && <BottomSpacer />}
                    </>
                )}
            </TabList>

            {/* Confirm Action Sheet */}
            <BottomSheet
                isOpen={!!confirmAction}
                onClose={() => { setConfirmAction(null); setCancelReason(''); }}
                title={confirmAction?.type === 'close' ? 'Fechar comanda' : 'Cancelar comanda'}
                size={confirmAction?.type === 'cancel' ? undefined : 'small'}
            >
                <ConfirmContent>
                    <ConfirmIcon $danger={confirmAction?.type === 'cancel'}>
                        <AlertTriangle size={24} color={confirmAction?.type === 'cancel' ? colors.danger : colors.occasionalPurple} />
                    </ConfirmIcon>
                    <ConfirmMessage>
                        {confirmAction?.type === 'close'
                            ? `Deseja fechar a comanda "${confirmAction?.tab?.table_identifier}"? O cliente será cobrado.`
                            : `Deseja cancelar a comanda "${confirmAction?.tab?.table_identifier}"? Esta ação não pode ser desfeita.`
                        }
                    </ConfirmMessage>
                    {confirmAction?.type === 'cancel' && (
                        <CancelReasonField>
                            <CancelReasonTextarea
                                placeholder="Motivo do cancelamento (opcional)"
                                value={cancelReason}
                                onChange={e => setCancelReason(e.target.value)}
                                rows={3}
                                maxLength={255}
                            />
                        </CancelReasonField>
                    )}
                    <ConfirmBtns>
                        <ConfirmBackBtn onClick={() => { setConfirmAction(null); setCancelReason(''); }}>
                            Voltar
                        </ConfirmBackBtn>
                        <ConfirmActionBtn
                            $danger={confirmAction?.type === 'cancel'}
                            onClick={handleConfirmedAction}
                        >
                            {confirmAction?.type === 'close' ? 'Fechar' : 'Cancelar comanda'}
                        </ConfirmActionBtn>
                    </ConfirmBtns>
                </ConfirmContent>
            </BottomSheet>

            {/* Detail Sheet */}
            <BottomSheet
                isOpen={!!detailTab}
                onClose={handleCloseDetail}
                title="Detalhes da comanda"
                size="large"
            >
                {detailLoading && (
                    <DetailLoadingWrap>
                        <Spinner />
                    </DetailLoadingWrap>
                )}
                {detailData && !paymentStep && (
                    <DetailContent>
                        <DetailMeta>
                            <DetailMetaRow>
                                <DetailMetaLabel>Mesa / ID</DetailMetaLabel>
                                <DetailMetaValue>{detailData.table_identifier}</DetailMetaValue>
                            </DetailMetaRow>
                            <DetailMetaRow>
                                <DetailMetaLabel>Cliente</DetailMetaLabel>
                                <DetailMetaValue>{detailData.person_name}</DetailMetaValue>
                            </DetailMetaRow>
                            {detailData.created_at && (
                                <DetailMetaRow>
                                    <DetailMetaLabel>Aberta há</DetailMetaLabel>
                                    <DetailMetaValue>{timeAgo(detailData.created_at)}</DetailMetaValue>
                                </DetailMetaRow>
                            )}
                        </DetailMeta>

                        <DetailItemsTitle>
                            Itens ({detailData.items?.length || 0})
                        </DetailItemsTitle>

                        <ItemsList>
                            {detailData.items?.map(item => (
                                item.type === 'ticket' ? (
                                    <DetailItem key={item.id}>
                                        <DetailItemHeader>
                                            <DetailItemQty>🎟</DetailItemQty>
                                            <DetailItemName>{item.product_name}</DetailItemName>
                                            <DetailItemTotal>{formatPrice(item.total_price)}</DetailItemTotal>
                                            {isEstablishmentSession && (
                                                <DeleteItemBtn onClick={() => setDeleteItemTarget(item)}>
                                                    <Trash2 size={14} color={colors.danger} />
                                                </DeleteItemBtn>
                                            )}
                                        </DetailItemHeader>
                                        {item.ticket_code && (
                                            <DetailTicketCode>Código: {item.ticket_code}</DetailTicketCode>
                                        )}
                                        {item.created_by?.name && (
                                            <DetailItemWaiter>🧑‍🍳 Garçom: {item.created_by.name}</DetailItemWaiter>
                                        )}
                                    </DetailItem>
                                ) : (
                                    <DetailItem key={item.id}>
                                        <DetailItemHeader>
                                            <DetailItemQty>{item.quantity}x</DetailItemQty>
                                            <DetailItemName>{item.product_name}</DetailItemName>
                                            <DetailItemTotal>{formatPrice(item.total_price)}</DetailItemTotal>
                                            {isEstablishmentSession && (
                                                <DeleteItemBtn
                                                    onClick={() => setDeleteItemTarget(item)}
                                                    aria-label="Remover item"
                                                >
                                                    <Trash2 size={14} color={colors.danger} />
                                                </DeleteItemBtn>
                                            )}
                                        </DetailItemHeader>
                                        {item.options?.length > 0 && (
                                            <DetailOptions>
                                                {item.options.map(opt => (
                                                    <DetailOption key={opt.id}>
                                                        + {opt.name}
                                                        {Number(opt.price_adjustment) > 0 && (
                                                            <span> (+{formatPrice(opt.price_adjustment)})</span>
                                                        )}
                                                    </DetailOption>
                                                ))}
                                            </DetailOptions>
                                        )}
                                        {item.observation && (
                                            <DetailObs>"{item.observation}"</DetailObs>
                                        )}
                                        {item.created_by?.name && (
                                            <DetailItemWaiter>🧑‍🍳 Garçom: {item.created_by.name}</DetailItemWaiter>
                                        )}
                                    </DetailItem>
                                )
                            ))}
                        </ItemsList>

                        <DetailTotalRow>
                            <DetailTotalLabel>Total</DetailTotalLabel>
                            <DetailTotalValue>{formatPrice(detailData.total)}</DetailTotalValue>
                        </DetailTotalRow>

                        {detailData.status !== 'closed' && (
                            <>
                                <TipToggleRow
                                    $disabled={Number(detailData.total || 0) === 0}
                                    onClick={() => Number(detailData.total || 0) > 0 && setTipEnabled(prev => !prev)}
                                >
                                    <TipToggleLabel>
                                        <TipToggleTitle>Taxa de serviço - 10%</TipToggleTitle>
                                        <TipToggleHint>
                                            {tipEnabled && Number(detailData.total || 0) > 0
                                                ? `+ ${formatPrice(Number(detailData.total || 0) * 0.1)}`
                                                : 'Taxa de serviço do garçom'
                                            }
                                        </TipToggleHint>
                                    </TipToggleLabel>
                                    <TipSwitch $active={tipEnabled && Number(detailData.total || 0) > 0}>
                                        <TipSwitchKnob $active={tipEnabled && Number(detailData.total || 0) > 0} />
                                    </TipSwitch>
                                </TipToggleRow>

                                {tipEnabled && (
                                    <DetailTotalRow>
                                        <DetailTotalLabel>Total com taxa de serviço</DetailTotalLabel>
                                        <DetailTotalValue>{formatPrice(Number(detailData.total || 0) * 1.1)}</DetailTotalValue>
                                    </DetailTotalRow>
                                )}

                                {isEstablishmentSession && (
                                    <CloseCommandBtn onClick={handleOpenPaymentStep}>
                                        Fechar comanda
                                    </CloseCommandBtn>
                                )}
                            </>
                        )}

                        {detailData.status === 'closed' && detailData.tip_value > 0 && (
                            <DetailMetaRow>
                                <DetailMetaLabel>Taxa de serviço (10%)</DetailMetaLabel>
                                <DetailMetaValue>{formatPrice(detailData.tip_value)}</DetailMetaValue>
                            </DetailMetaRow>
                        )}
                    </DetailContent>
                )}

                {detailData && paymentStep && (
                    <DetailContent>
                        {/* Compact summary header */}
                        <PaymentSummaryBar>
                            <PaymentBackBtn onClick={() => setPaymentStep(false)}>
                                ← Voltar
                            </PaymentBackBtn>
                            <PaymentSummaryPill>
                                {detailData.table_identifier} · {detailData.person_name} · {formatPrice(tipEnabled ? Number(detailData.total || 0) * 1.1 : detailData.total)}
                                {tipEnabled && <TipBadge>+10%</TipBadge>}
                            </PaymentSummaryPill>
                        </PaymentSummaryBar>

                        {/* Simple payment - default 1 customer (G) */}
                        {!splitExpanded && (
                            <SimplePaymentSection>
                                <CustomerLabel>Forma de pagamento</CustomerLabel>
                                {paymentMethodsLoading ? (
                                    <PaymentMethodsLoading><Spinner /></PaymentMethodsLoading>
                                ) : (
                                    <MethodChips>
                                        {paymentMethods.map(method => (
                                            <MethodChip
                                                key={method.id}
                                                $selected={selectedMethods[0] === method.id}
                                                onClick={() => handleSelectMethod(0, method.id)}
                                                type="button"
                                            >
                                                {method.name}
                                            </MethodChip>
                                        ))}
                                    </MethodChips>
                                )}
                            </SimplePaymentSection>
                        )}

                        {/* Divide toggle (G) */}
                        <DivideToggle onClick={() => {
                            const next = !splitExpanded;
                            setSplitExpanded(next);
                            if (!next) {
                                setCustomerCount(1);
                                setSelectedMethods([selectedMethods[0] || null]);
                            }
                        }}>
                            <DivideToggleText>
                                {splitExpanded ? 'Pagamento simples' : 'Dividir conta'}
                            </DivideToggleText>
                            <DivideToggleIcon $expanded={splitExpanded}>▾</DivideToggleIcon>
                        </DivideToggle>

                        {/* Expanded split view (G) */}
                        {splitExpanded && (
                            <>
                                <StepperSection>
                                    <StepperLabel>Dividir entre</StepperLabel>
                                    <StepperRow>
                                        <StepperBtn
                                            onClick={() => handleChangeCustomerCount(-1)}
                                            disabled={customerCount <= 1}
                                            aria-label="Remover cliente"
                                        >
                                            <Minus size={20} strokeWidth={2.5} />
                                        </StepperBtn>
                                        <StepperValue>
                                            {customerCount} {customerCount === 1 ? 'cliente' : 'clientes'}
                                        </StepperValue>
                                        <StepperBtn
                                            onClick={() => handleChangeCustomerCount(1)}
                                            disabled={customerCount >= 20}
                                            aria-label="Adicionar cliente"
                                        >
                                            <Plus size={20} strokeWidth={2.5} />
                                        </StepperBtn>
                                    </StepperRow>
                                    {customerCount > 1 && (
                                        <PerPersonAmount>
                                            {formatPrice((tipEnabled ? Number(detailData.total || 0) * 1.1 : Number(detailData.total || 0)) / customerCount)} por pessoa
                                        </PerPersonAmount>
                                    )}
                                </StepperSection>

                                <CustomerRowsSection>
                                    {paymentMethodsLoading ? (
                                        <PaymentMethodsLoading><Spinner /></PaymentMethodsLoading>
                                    ) : (
                                        Array.from({ length: customerCount }, (_, i) => (
                                            <CustomerRow key={i}>
                                                <CustomerLabel>Cliente {i + 1}</CustomerLabel>
                                                <MethodChips>
                                                    {paymentMethods.map(method => (
                                                        <MethodChip
                                                            key={method.id}
                                                            $selected={selectedMethods[i] === method.id}
                                                            onClick={() => handleSelectMethod(i, method.id)}
                                                            type="button"
                                                        >
                                                            {method.name}
                                                        </MethodChip>
                                                    ))}
                                                </MethodChips>
                                            </CustomerRow>
                                        ))
                                    )}
                                </CustomerRowsSection>
                            </>
                        )}

                        <ConfirmPrimaryBtn
                            onClick={handleCloseFromDetail}
                            disabled={closing || paymentMethodsLoading || selectedMethods.some(m => m === null)}
                        >
                            {closing ? 'Fechando...' : 'Confirmar fechamento'}
                        </ConfirmPrimaryBtn>
                    </DetailContent>
                )}
            </BottomSheet>

            {/* Delete Item Confirm Sheet */}
            <BottomSheet
                isOpen={!!deleteItemTarget}
                onClose={() => setDeleteItemTarget(null)}
                title="Remover item"
                size="small"
            >
                <ConfirmContent>
                    <ConfirmIcon $danger>
                        <Trash2 size={24} color={colors.danger} />
                    </ConfirmIcon>
                    <ConfirmMessage>
                        Deseja remover <strong>{deleteItemTarget?.quantity}x {deleteItemTarget?.product_name}</strong> desta comanda?
                    </ConfirmMessage>
                    <ConfirmBtns>
                        <ConfirmBackBtn onClick={() => setDeleteItemTarget(null)}>Cancelar</ConfirmBackBtn>
                        <ConfirmActionBtn $danger onClick={handleDeleteItem} disabled={deletingItem}>
                            {deletingItem ? 'Removendo...' : 'Remover'}
                        </ConfirmActionBtn>
                    </ConfirmBtns>
                </ConfirmContent>
            </BottomSheet>

            {/* Logout Sheet */}
            <BottomSheet
                isOpen={logoutSheet}
                onClose={() => setLogoutSheet(false)}
                title="Encerrar turno"
                size="small"
            >
                <ConfirmContent>
                    <ConfirmIcon $danger>
                        <LogOut size={24} color={colors.danger} />
                    </ConfirmIcon>
                    <ConfirmMessage>Deseja encerrar o turno? Você precisará inserir seu código novamente para acessar.</ConfirmMessage>
                    <ConfirmBtns>
                        <ConfirmBackBtn onClick={() => setLogoutSheet(false)}>Cancelar</ConfirmBackBtn>
                        <ConfirmActionBtn $danger onClick={handleLogout}>Encerrar</ConfirmActionBtn>
                    </ConfirmBtns>
                </ConfirmContent>
            </BottomSheet>

            {/* Floating Batch Bar */}
            {selectMode && selectedIds.size > 0 && (
                <FloatingBar>
                    <FloatingBarInfo>
                        <FloatingBarCount>{selectedIds.size} comanda{selectedIds.size !== 1 ? 's' : ''}</FloatingBarCount>
                        <FloatingBarTotal>{formatPrice(selectedTotal)}</FloatingBarTotal>
                    </FloatingBarInfo>
                    <FloatingBarBtn onClick={handleOpenBatchClose}>
                        Fechar selecionadas
                    </FloatingBarBtn>
                </FloatingBar>
            )}

            {/* Batch Close Sheet */}
            <BottomSheet
                isOpen={batchSheet}
                onClose={() => { if (!batchClosing) setBatchSheet(false); }}
                title="Fechar comandas em lote"
                size="large"
            >
                <DetailContent>
                    <BatchSummaryList>
                        {batchDetailsLoading && (
                            <DetailLoadingWrap>
                                <Spinner />
                            </DetailLoadingWrap>
                        )}
                        {!batchDetailsLoading && selectedTabs.map(tab => {
                            const detail = batchDetails[tab.id];
                            const isExpanded = batchExpandedIds.has(tab.id);
                            return (
                                <BatchDetailCard key={tab.id} $expanded={isExpanded}>
                                    <BatchDetailCardHeader onClick={() => handleToggleBatchExpand(tab.id)}>
                                        <BatchDetailCardInfo>
                                            <BatchDetailCardTitle>{tab.table_identifier}</BatchDetailCardTitle>
                                            <BatchDetailCardSub>{tab.person_name}</BatchDetailCardSub>
                                        </BatchDetailCardInfo>
                                        <BatchDetailCardRight>
                                            <BatchSummaryValue>{formatPrice(tab.total)}</BatchSummaryValue>
                                            <BatchDetailChevron $expanded={isExpanded}>
                                                <ChevronDown size={18} color={colors.nightLight} />
                                            </BatchDetailChevron>
                                        </BatchDetailCardRight>
                                    </BatchDetailCardHeader>
                                    {isExpanded && detail && (
                                        <BatchDetailCardBody>
                                            <DetailItemsTitle>
                                                Itens ({detail.items?.length || 0})
                                            </DetailItemsTitle>
                                            <ItemsList>
                                                {detail.items?.map(item => (
                                                    <DetailItem key={item.id}>
                                                        <DetailItemHeader>
                                                            <DetailItemQty>{item.quantity}x</DetailItemQty>
                                                            <DetailItemName>{item.product_name}</DetailItemName>
                                                            <DetailItemTotal>{formatPrice(item.total_price)}</DetailItemTotal>
                                                        </DetailItemHeader>
                                                        {item.options?.length > 0 && (
                                                            <DetailOptions>
                                                                {item.options.map(opt => (
                                                                    <DetailOption key={opt.id}>
                                                                        + {opt.name}
                                                                        {Number(opt.price_adjustment) > 0 && (
                                                                            <span> (+{formatPrice(opt.price_adjustment)})</span>
                                                                        )}
                                                                    </DetailOption>
                                                                ))}
                                                            </DetailOptions>
                                                        )}
                                                        {item.observation && (
                                                            <DetailObs>"{item.observation}"</DetailObs>
                                                        )}
                                                        {item.created_by?.name && (
                                                            <DetailItemWaiter>Garçom: {item.created_by.name}</DetailItemWaiter>
                                                        )}
                                                    </DetailItem>
                                                ))}
                                            </ItemsList>
                                        </BatchDetailCardBody>
                                    )}
                                    {isExpanded && !detail && (
                                        <BatchDetailCardBody>
                                            <DetailLoadingWrap>
                                                <Spinner />
                                            </DetailLoadingWrap>
                                        </BatchDetailCardBody>
                                    )}
                                </BatchDetailCard>
                            );
                        })}
                    </BatchSummaryList>

                    <DetailTotalRow>
                        <DetailTotalLabel>Subtotal</DetailTotalLabel>
                        <DetailTotalValue>{formatPrice(selectedTotal)}</DetailTotalValue>
                    </DetailTotalRow>

                    <TipToggleRow onClick={() => setBatchTipEnabled(prev => !prev)}>
                        <TipToggleLabel>
                            <TipToggleTitle>Taxa de serviço - 10%</TipToggleTitle>
                            <TipToggleHint>
                                {batchTipEnabled
                                    ? `+ ${formatPrice(selectedTotal * 0.1)}`
                                    : 'Taxa de serviço do garçom'
                                }
                            </TipToggleHint>
                        </TipToggleLabel>
                        <TipSwitch $active={batchTipEnabled}>
                            <TipSwitchKnob $active={batchTipEnabled} />
                        </TipSwitch>
                    </TipToggleRow>

                    {batchTipEnabled && (
                        <DetailTotalRow>
                            <DetailTotalLabel>Total com taxa de serviço</DetailTotalLabel>
                            <DetailTotalValue>{formatPrice(selectedTotal * 1.1)}</DetailTotalValue>
                        </DetailTotalRow>
                    )}

                    {/* Payment method selection */}
                    <CustomerRowsSection>
                        <CustomerLabel>Forma de pagamento (aplicada a todas)</CustomerLabel>
                        {paymentMethodsLoading ? (
                            <PaymentMethodsLoading><Spinner /></PaymentMethodsLoading>
                        ) : (
                            <MethodChips>
                                {paymentMethods.map(method => (
                                    <MethodChip
                                        key={method.id}
                                        $selected={batchPaymentMethod === method.id}
                                        onClick={() => setBatchPaymentMethod(method.id)}
                                        type="button"
                                    >
                                        {method.name}
                                    </MethodChip>
                                ))}
                            </MethodChips>
                        )}
                    </CustomerRowsSection>

                    {/* Progress bar */}
                    {batchClosing && (
                        <BatchProgressWrap>
                            <BatchProgressText>
                                Fechando {batchProgress.done} de {batchProgress.total}...
                            </BatchProgressText>
                            <BatchProgressBar>
                                <BatchProgressFill
                                    style={{ width: `${batchProgress.total ? (batchProgress.done / batchProgress.total) * 100 : 0}%` }}
                                />
                            </BatchProgressBar>
                        </BatchProgressWrap>
                    )}

                    {/* Error display */}
                    {!batchClosing && batchProgress.errors.length > 0 && (
                        <BatchErrorWrap>
                            <AlertTriangle size={16} color={colors.danger} />
                            <BatchErrorText>
                                Falha ao fechar: {batchProgress.errors.join(', ')}
                            </BatchErrorText>
                        </BatchErrorWrap>
                    )}

                    <ConfirmPrimaryBtn
                        onClick={handleBatchCloseConfirm}
                        disabled={batchClosing || paymentMethodsLoading || !batchPaymentMethod}
                    >
                        {batchClosing ? 'Fechando...' : 'Confirmar fechamento'}
                    </ConfirmPrimaryBtn>
                </DetailContent>
            </BottomSheet>

            {/* New Tab Sheet */}
            <BottomSheet
                isOpen={newTabSheet}
                onClose={() => { if (!creating && !eventsLoading) { setNewTabSheet(false); setNewTabStep(1); setTicketSelections([]); } }}
                title={newTabStep === 1 ? 'Nova comanda' : 'Ingressos'}
                size={newTabStep === 2 ? 'large' : 'small'}
            >
                {newTabStep === 1 && (
                    <SheetForm>
                        <FormField>
                            <FormLabel>Nome do cliente</FormLabel>
                            <FormInput
                                placeholder="Ex: João Silva"
                                value={personName}
                                onChange={e => setPersonName(e.target.value)}
                                autoFocus
                            />
                        </FormField>
                        <FormField>
                            <FormLabel>Identificador da mesa</FormLabel>
                            <FormInput
                                placeholder="Ex: Mesa 5, telefone, CPF..."
                                value={tableIdentifier}
                                onChange={e => setTableIdentifier(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && canSubmit && !creating && !eventsLoading && handleNextStep()}
                            />
                        </FormField>
                        <CreateBtn onClick={handleNextStep} disabled={!canSubmit || creating || eventsLoading}>
                            {(creating || eventsLoading) ? 'Aguarde...' : 'Próximo →'}
                        </CreateBtn>
                    </SheetForm>
                )}

                {newTabStep === 2 && (
                    <SheetForm>
                        {activeEvents.map(event => (
                            <NewTabEventSection key={event.id}>
                                <NewTabEventTitle>
                                    🎟 {event.name}
                                    {event.date && (
                                        <NewTabEventDate>
                                            {' · '}{new Date(event.date).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
                                        </NewTabEventDate>
                                    )}
                                </NewTabEventTitle>
                                {event.batches?.map(batch => {
                                    const sel = ticketSelections.find(s => s.batch.id === batch.id);
                                    return (
                                        <NewTabBatchRow key={batch.id}>
                                            <NewTabBatchInfo>
                                                <NewTabBatchName>{batch.name}</NewTabBatchName>
                                                <NewTabBatchMeta>
                                                    {Number(batch.price || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })} · {batch.available} disp.
                                                </NewTabBatchMeta>
                                            </NewTabBatchInfo>
                                            <NewTabBatchRight>
                                                {sel ? (
                                                    <>
                                                        <NewTabQtyBtn onClick={() => handleSetTicketQty(event, batch, sel.quantity - 1)}>
                                                            <Minus size={14} />
                                                        </NewTabQtyBtn>
                                                        <NewTabBatchQty>{sel.quantity}</NewTabBatchQty>
                                                        <NewTabQtyBtn
                                                            onClick={() => handleSetTicketQty(event, batch, sel.quantity + 1)}
                                                            disabled={sel.quantity >= (batch.available ?? 99)}
                                                        >
                                                            <Plus size={14} />
                                                        </NewTabQtyBtn>
                                                    </>
                                                ) : (
                                                    <NewTabBatchBtn onClick={() => handleSetTicketQty(event, batch, 1)}>
                                                        +
                                                    </NewTabBatchBtn>
                                                )}
                                            </NewTabBatchRight>
                                        </NewTabBatchRow>
                                    );
                                })}
                            </NewTabEventSection>
                        ))}

                        <NewTabActions>
                            <SkipBtn onClick={handleCreateTab} disabled={creating}>
                                {creating ? 'Abrindo...' : 'Pular'}
                            </SkipBtn>
                            <CreateBtn
                                onClick={ticketSelections.length > 0 ? handleCreateWithTickets : handleCreateTab}
                                disabled={creating}
                                style={{ flex: 2 }}
                            >
                                {creating ? 'Abrindo...' : `Criar comanda${ticketSelections.length > 0 ? ` (+${ticketSelections.reduce((s, x) => s + x.quantity, 0)} ingresso${ticketSelections.reduce((s, x) => s + x.quantity, 0) !== 1 ? 's' : ''})` : ''}`}
                            </CreateBtn>
                        </NewTabActions>
                    </SheetForm>
                )}
            </BottomSheet>


            {/* Orders Sheet */}
            <BottomSheet
                isOpen={ordersSheet.open}
                onClose={() => setOrdersSheet({ open: false, tab: null, data: null, loading: false })}
                title={`Pedidos em andamento${ordersSheet.tab ? ` — ${ordersSheet.tab.table_identifier}` : ''}`}
                size="large"
            >
                <OrdersSheetBody>
                <OrdersSheetHeader>
                    <OrdersRefreshBtn onClick={handleRefreshOrdersSheet} disabled={ordersSheet.loading}>
                        {ordersSheet.loading ? <Spinner style={{ width: 16, height: 16 }} /> : 'Atualizar'}
                    </OrdersRefreshBtn>
                </OrdersSheetHeader>

                {ordersSheet.loading && !ordersSheet.data && (
                    <DetailLoadingWrap>
                        <Spinner />
                    </DetailLoadingWrap>
                )}

                {ordersSheet.data && (() => {
                    const activeOrders = (Array.isArray(ordersSheet.data) ? ordersSheet.data : [])
                        .filter(o => ACTIVE_ORDER_STATUSES.includes(o.status));
                    if (activeOrders.length === 0) {
                        return (
                            <EmptyState>
                                <EmptyIconWrap>
                                    <Check size={28} color={colors.nightLight} />
                                </EmptyIconWrap>
                                <EmptyTitle>Nenhum pedido em andamento</EmptyTitle>
                                <EmptyHint>Todos os pedidos foram entregues ou não há pedidos ativos</EmptyHint>
                            </EmptyState>
                        );
                    }
                    return (
                        <OrdersList>
                            {activeOrders.map(order => (
                                <OrderRow key={order.id}>
                                    <OrderRowHeader>
                                        <OrderStatusBadge $color={ORDER_STATUS_COLORS[order.status]}>
                                            {ORDER_STATUS_LABELS[order.status] || order.status}
                                        </OrderStatusBadge>
                                        {order.station_name && (
                                            <OrderStation>{order.station_name}</OrderStation>
                                        )}
                                    </OrderRowHeader>
                                    {order.items?.map((item, i) => (
                                        <OrderItem key={i}>
                                            {item.product_image?.thumb && (
                                                <OrderItemImage src={item.product_image.thumb} alt={item.product_name} />
                                            )}
                                            <OrderItemInfo>
                                                <span>{item.quantity}x {item.product_name}</span>
                                                {item.options?.length > 0 && (
                                                    <OrderItemOptions>
                                                        {item.options.map(opt => opt.name).join(', ')}
                                                    </OrderItemOptions>
                                                )}
                                            </OrderItemInfo>
                                        </OrderItem>
                                    ))}
                                    {order.observation && (
                                        <DetailObs>"{order.observation}"</DetailObs>
                                    )}
                                    {order.created_by?.name && (
                                        <DetailItemWaiter>🧑‍🍳 Garçom: {order.created_by.name}</DetailItemWaiter>
                                    )}
                                </OrderRow>
                            ))}
                        </OrdersList>
                    );
                })()}
                </OrdersSheetBody>
            </BottomSheet>
        </Screen>
    );
}

/* ───── Animations ───── */

const fadeSlideIn = keyframes`
    from { opacity: 0; transform: translateY(12px); }
    to   { opacity: 1; transform: translateY(0); }
`;

const spin = keyframes`
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
`;

const pulse = keyframes`
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
`;

const pulseRing = keyframes`
    0%   { transform: scale(0.8); opacity: 0.8; }
    100% { transform: scale(2.2); opacity: 0; }
`;

const ReadyDotWrap = styled.span`
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 10px;
    height: 10px;
    margin-left: 5px;
    flex-shrink: 0;
`;

const ReadyDotInner = styled.span`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${colors.occasionalGreen};
    position: relative;
    z-index: 1;
`;

const ReadyDotRing = styled.span`
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: ${colors.occasionalGreen};
    animation: ${pulseRing} 1.5s ease-out infinite;
`;

/* ───── Layout ───── */

const Screen = styled.div`
    display: flex;
    flex-direction: column;
    min-height: 100%;
    background: #f5f5f7;
`;

const PageHeader = styled.div`
    display: flex;
    flex-direction: column;
    background: #fff;
    border-bottom: 1px solid ${colors.neutralDark};
    position: sticky;
    top: 0;
    z-index: 10;
`;

const ViewTabs = styled.div`
    display: flex;
    padding: 0 20px;
    gap: 0;
    border-bottom: none;
`;

const ViewTab = styled.button`
    flex: 1;
    padding: 12px 0;
    font-size: 14px;
    font-weight: 600;
    border: none;
    cursor: pointer;
    background: transparent;
    color: ${({ $active }) => $active ? colors.occasionalPurple : colors.nightLight};
    border-bottom: 2.5px solid ${({ $active }) => $active ? colors.occasionalPurple : 'transparent'};
    transition: all 0.15s;
    &:hover { color: ${({ $active }) => $active ? colors.occasionalPurple : colors.night}; }
`;

const PeriodFilter = styled.div`
    display: flex;
    gap: 6px;
    padding: 10px 20px 14px;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    &::-webkit-scrollbar { display: none; }
`;

const PeriodChip = styled.button`
    flex-shrink: 0;
    padding: 5px 14px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
    border: 1.5px solid ${({ $active }) => $active ? colors.occasionalPurple : colors.neutralDark};
    background: ${({ $active }) => $active ? colors.occasionalPurple : 'transparent'};
    color: ${({ $active }) => $active ? '#fff' : colors.nightLight};
    cursor: pointer;
    transition: all 0.15s;
    &:hover {
        border-color: ${({ $active }) => $active ? colors.occasionalPurple : colors.night};
        color: ${({ $active }) => $active ? '#fff' : colors.night};
    }
`;

const HeaderRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 20px 18px;
`;

const HeaderLeft = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 10px;
`;

const HeaderTitleGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const HeaderRight = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const LogoutBtn = styled.button`
    width: 44px;
    height: 44px;
    border-radius: 14px;
    background: ${colors.danger}12;
    border: 1.5px solid ${colors.danger}30;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${colors.danger};
    transition: all 0.15s;
    &:active { transform: scale(0.95); }
`;

const SearchToggleBtn = styled.button`
    width: 44px;
    height: 44px;
    border-radius: 14px;
    background: ${({ $active }) => $active ? `${colors.occasionalPurple}15` : colors.neutralDark};
    border: 1.5px solid ${({ $active }) => $active ? `${colors.occasionalPurple}40` : 'transparent'};
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${({ $active }) => $active ? colors.occasionalPurple : colors.night};
    transition: all 0.15s;
    &:active { transform: scale(0.95); }
`;

const SelectModeBtn = styled.button`
    width: 44px;
    height: 44px;
    border-radius: 14px;
    background: ${({ $active }) => $active ? `${colors.occasionalPurple}15` : colors.neutralDark};
    border: 1.5px solid ${({ $active }) => $active ? `${colors.occasionalPurple}40` : 'transparent'};
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${({ $active }) => $active ? colors.occasionalPurple : colors.night};
    transition: all 0.15s;
    &:active { transform: scale(0.95); }
`;

const AlwaysVisibleSearchBar = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0 20px 14px;
    background: #f5f5f7;
    border: 1.5px solid ${colors.neutralDark};
    border-radius: 10px;
    padding: 0 12px;
    min-height: 40px;
    position: relative;
    transition: border-color 0.15s;
    &:focus-within { border-color: ${colors.occasionalPurple}; background: #fff; }
`;

const SearchIconWrap = styled.div`
    display: flex;
    flex-shrink: 0;
`;

const AlwaysVisibleSearchInput = styled.input`
    flex: 1;
    background: none;
    border: none;
    outline: none;
    font-size: 15px;
    color: ${colors.nightDark};
    height: 40px;
    &::placeholder { color: ${colors.nightLight}; }
`;

const ClearInputBtn = styled.button`
    position: absolute;
    right: 10px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: ${colors.nightLight};
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: opacity 0.15s;
    flex-shrink: 0;
`;

const HeaderTitle = styled.h1`
    font-size: 22px;
    font-weight: 700;
    color: ${colors.nightDark};
    margin: 0;
`;

const HeaderCount = styled.span`
    font-size: 13px;
    font-weight: 500;
    color: ${colors.nightLight};
`;

const WaiterIdentity = styled.span`
    font-size: 11px;
    font-weight: 600;
    color: ${colors.occasionalPurple};
    opacity: 0.8;
`;

const NewTabBtn = styled.button`
    width: 44px;
    height: 44px;
    border-radius: 14px;
    background: ${colors.occasionalPurple};
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.15s;
    box-shadow: 0 2px 12px ${colors.occasionalPurple}40;
    &:hover { opacity: 0.9; transform: scale(1.05); }
    &:active { transform: scale(0.95); }
`;

/* ───── Tab List ───── */

const TabList = styled.div`
    flex: 1;
    padding: 14px 16px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    overflow-y: auto;
`;

const BottomSpacer = styled.div`height: ${({ $extraSpace }) => $extraSpace ? '100px' : '20px'};`;

/* ───── Tab Card ───── */

const TabCard = styled.div`
    background: ${({ $selected }) => $selected ? `${colors.occasionalPurple}08` : '#fff'};
    border-radius: 14px;
    border: 1.5px solid ${({ $selected, $ready }) =>
        $selected ? colors.occasionalPurple :
        $ready ? colors.occasionalGreen :
        colors.neutralDark};
    cursor: pointer;
    overflow: hidden;
    box-shadow: ${({ $selected, $ready }) =>
        $selected ? `0 1px 8px ${colors.occasionalPurple}20` :
        $ready ? `0 0 0 3px ${colors.occasionalGreen}22, 0 2px 8px rgba(0,0,0,0.08)` :
        '0 1px 4px rgba(0,0,0,0.06)'};
    animation: ${fadeSlideIn} 0.3s ease both;
    animation-delay: ${({ $index }) => Math.min($index * 0.04, 0.3)}s;
    transition: border-color 0.2s, box-shadow 0.2s;
    &:hover { box-shadow: ${({ $ready }) => $ready ? `0 0 0 3px ${colors.occasionalGreen}30, 0 4px 12px rgba(0,0,0,0.1)` : '0 4px 12px rgba(0,0,0,0.1)'}; transform: translateY(-1px); }
    &:active { transform: scale(0.99); }
`;

const ReadyBanner = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 7px 14px;
    background: ${colors.occasionalGreen}12;
    border-bottom: 1px solid ${colors.occasionalGreen}30;
    font-size: 12px;
    font-weight: 700;
    color: ${colors.occasionalGreen};
`;

const TabCardMain = styled.div`
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 16px 14px 14px 16px;
`;

const TabStatusCol = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    min-width: 36px;
`;

const StatusDot = styled.div`
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #2BC866;
    box-shadow: 0 0 0 3px #2BC86625;
`;

const ClosedDot = styled.div`
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${colors.nightLight};
    box-shadow: 0 0 0 3px ${colors.nightLight}25;
`;

const TimeTag = styled.span`
    display: flex;
    align-items: center;
    gap: 3px;
    font-size: 10px;
    font-weight: 500;
    color: ${colors.night};
    white-space: nowrap;
`;

const TabInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 3px;
    flex: 1;
    min-width: 0;
`;

const TabIdentifier = styled.span`
    font-weight: 700;
    font-size: 16px;
    color: ${colors.nightDark};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const TabPerson = styled.span`
    font-size: 13px;
    color: ${colors.nightLight};
`;

const TabWaiter = styled.span`
    font-size: 12px;
    color: ${colors.nightLight};
    margin-top: 1px;
`;

const TabRight = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
`;

const TabTotal = styled.span`
    font-size: 15px;
    font-weight: 700;
    color: ${colors.occasionalPurple};
`;

const TabArrow = styled.div`
    opacity: 0.4;
`;

/* ───── Tab Actions ───── */

const TabActions = styled.div`
    display: flex;
    border-top: 1px solid ${colors.neutralDark};
`;

const TabActionDivider = styled.div`
    width: 1px;
    background: ${colors.neutralDark};
`;

const TabActionBtn = styled.button`
    flex: 1;
    padding: 12px 8px;
    font-size: 13px;
    font-weight: 600;
    border: none;
    cursor: pointer;
    background: transparent;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${({ $variant }) =>
        $variant === 'cancel' ? colors.danger :
            $variant === 'close' ? colors.occasionalPurple :
                $variant === 'orders' ? colors.occasionalOrange :
                    colors.night};
    transition: background 0.15s;
    &:hover {
        background: ${({ $variant }) =>
        $variant === 'cancel' ? `${colors.danger}0A` :
            $variant === 'close' ? `${colors.occasionalPurple}0A` :
                $variant === 'orders' ? `${colors.occasionalOrange}0A` :
                    colors.neutralDark};
    }
    &:active { opacity: 0.7; }
`;

/* ───── Loading & Empty ───── */

const LoadingWrap = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    gap: 16px;
`;

const Spinner = styled.div`
    width: 32px;
    height: 32px;
    border: 3px solid ${colors.neutralDark};
    border-top-color: ${colors.occasionalPurple};
    border-radius: 50%;
    animation: ${spin} 0.7s linear infinite;
`;

const LoadingText = styled.span`
    font-size: 14px;
    color: ${colors.nightLight};
    animation: ${pulse} 1.5s ease infinite;
`;

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 56px 24px 40px;
    text-align: center;
`;

const EmptyIconWrap = styled.div`
    width: 72px;
    height: 72px;
    border-radius: 20px;
    background: ${colors.neutralDark};
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 20px;
`;

const EmptyTitle = styled.p`
    font-weight: 700;
    font-size: 18px;
    color: ${colors.nightDark};
    margin: 0 0 8px;
`;

const EmptyHint = styled.p`
    font-size: 14px;
    color: ${colors.nightLight};
    margin: 0;
    max-width: 280px;
    line-height: 1.5;
`;

/* ───── Confirm Action Sheet ───── */

const ConfirmContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 16px;
    padding: 8px 0;
`;

const ConfirmIcon = styled.div`
    width: 56px;
    height: 56px;
    border-radius: 16px;
    background: ${({ $danger }) => $danger ? `${colors.danger}15` : `${colors.occasionalPurple}15`};
    display: flex;
    align-items: center;
    justify-content: center;
`;

const ConfirmMessage = styled.p`
    font-size: 15px;
    color: ${colors.night};
    margin: 0;
    line-height: 1.5;
    max-width: 300px;
`;

const CancelReasonField = styled.div`
    width: 100%;
    text-align: left;
`;

const CancelReasonTextarea = styled.textarea`
    width: 100%;
    padding: 12px 14px;
    border: 1.5px solid ${colors.neutralDark};
    border-radius: 10px;
    font-size: 14px;
    color: ${colors.nightDark};
    resize: none;
    outline: none;
    box-sizing: border-box;
    font-family: inherit;
    transition: border-color 0.15s;
    &::placeholder { color: #999; }
    &:focus { border-color: ${colors.danger}; }
`;

const ConfirmBtns = styled.div`
    display: flex;
    gap: 10px;
    width: 100%;
    margin-top: 4px;
`;

const ConfirmBackBtn = styled.button`
    flex: 1;
    padding: 15px;
    border-radius: 10px;
    background: transparent;
    color: ${colors.night};
    font-size: 16px;
    font-weight: 700;
    border: 1.5px solid ${colors.neutralDark};
    cursor: pointer;
    transition: background 0.15s;
    &:hover { background: ${colors.neutralDark}; }
`;

const ConfirmActionBtn = styled.button`
    flex: 1;
    padding: 15px;
    border-radius: 10px;
    background: ${({ $danger }) => $danger ? colors.danger : colors.occasionalPurple};
    color: #fff;
    font-size: 16px;
    font-weight: 700;
    border: none;
    cursor: pointer;
    transition: all 0.15s;
    &:hover { opacity: 0.9; }
    &:active { transform: scale(0.98); }
`;

/* ───── Detail Sheet ───── */

const DetailLoadingWrap = styled.div`
    display: flex;
    justify-content: center;
    padding: 40px 0;
`;

const DetailContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0;
`;

const DetailMeta = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 4px 0 16px;
    border-bottom: 1px solid ${colors.neutralDark};
    margin-bottom: 16px;
`;

const DetailMetaRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const DetailMetaLabel = styled.span`
    font-size: 13px;
    color: ${colors.nightLight};
    font-weight: 500;
`;

const DetailMetaValue = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const DetailItemsTitle = styled.h4`
    font-size: 14px;
    font-weight: 700;
    color: ${colors.night};
    margin: 0 0 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
`;

const ItemsList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 16px;
`;

const DetailItem = styled.div`
    padding: 12px 14px;
    background: #f5f5f7;
    border-radius: 10px;
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const DetailItemHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const DetailItemQty = styled.span`
    font-size: 13px;
    font-weight: 700;
    color: ${colors.occasionalPurple};
    min-width: 24px;
`;

const DetailItemName = styled.span`
    font-size: 14px;
    font-weight: 700;
    color: ${colors.nightDark};
    flex: 1;
`;

const DetailItemTotal = styled.span`
    font-size: 14px;
    font-weight: 700;
    color: ${colors.nightDark};
    flex-shrink: 0;
`;

const DeleteItemBtn = styled.button`
    width: 30px;
    height: 30px;
    border-radius: 8px;
    background: ${colors.danger}10;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin-left: 4px;
    transition: opacity 0.15s;
    &:hover { opacity: 0.75; }
`;

const DetailOptions = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding-left: 32px;
`;

const DetailOption = styled.span`
    font-size: 12px;
    color: ${colors.night};
`;

const DetailObs = styled.span`
    font-size: 12px;
    color: ${colors.nightLight};
    font-style: italic;
    padding-left: 32px;
`;

const DetailItemWaiter = styled.span`
    font-size: 11px;
    color: ${colors.nightLight};
    padding-left: 32px;
`;

const DetailTicketCode = styled.div`
    font-size: 12px;
    color: ${colors.nightLight};
    font-family: monospace;
    padding-left: 32px;
    margin-top: 2px;
    letter-spacing: 0.5px;
`;

const DetailTotalRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 0;
    border-top: 2px solid ${colors.nightDark};
    margin-bottom: 16px;
`;

const DetailTotalLabel = styled.span`
    font-size: 16px;
    font-weight: 700;
    color: ${colors.nightDark};
`;

const DetailTotalValue = styled.span`
    font-size: 22px;
    font-weight: 700;
    color: ${colors.occasionalPurple};
`;


const ConfirmPrimaryBtn = styled.button`
    flex: 1;
    padding: 15px;
    border-radius: 10px;
    background: ${({ disabled }) => disabled ? colors.neutralDark : colors.occasionalPurple};
    color: ${({ disabled }) => disabled ? colors.nightLight : '#fff'};
    font-size: 16px;
    font-weight: 700;
    border: none;
    cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
    transition: all 0.15s;
    &:hover:not(:disabled) { opacity: 0.9; }
    &:disabled { opacity: 0.5; }
`;

const CloseCommandBtn = styled.button`
    width: 100%;
    padding: 15px;
    border-radius: 10px;
    background: ${colors.occasionalPurple};
    color: #fff;
    font-size: 16px;
    font-weight: 700;
    border: none;
    cursor: pointer;
    transition: all 0.15s;
    &:hover { opacity: 0.9; }
    &:active { transform: scale(0.98); }
`;

/* ───── Simplified Payment (G) ───── */

const SimplePaymentSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-bottom: 8px;
`;

const DivideToggle = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    width: 100%;
    padding: 12px;
    background: transparent;
    border: 1.5px dashed ${colors.neutralDark};
    border-radius: 10px;
    cursor: pointer;
    margin-bottom: 16px;
    transition: all 0.15s;
    &:hover { border-color: ${colors.occasionalPurple}; background: ${colors.occasionalPurple}05; }
`;

const DivideToggleText = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: ${colors.occasionalPurple};
`;

const DivideToggleIcon = styled.span`
    font-size: 16px;
    color: ${colors.occasionalPurple};
    transition: transform 0.2s;
    transform: ${({ $expanded }) => $expanded ? 'rotate(180deg)' : 'rotate(0)'};
    display: inline-block;
`;

/* ───── Payment Step ───── */

const PaymentSummaryBar = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding-bottom: 16px;
    border-bottom: 1px solid ${colors.neutralDark};
    margin-bottom: 20px;
`;

const PaymentBackBtn = styled.button`
    background: none;
    border: none;
    font-size: 14px;
    font-weight: 600;
    color: ${colors.occasionalPurple};
    cursor: pointer;
    padding: 0;
    white-space: nowrap;
    flex-shrink: 0;
`;

const PaymentSummaryPill = styled.span`
    font-size: 13px;
    color: ${colors.nightLight};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
    text-align: right;
`;

const StepperSection = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    padding: 16px 0 20px;
    border-bottom: 1px solid ${colors.neutralDark};
    margin-bottom: 20px;
`;

const StepperLabel = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${colors.nightLight};
    text-transform: uppercase;
    letter-spacing: 0.5px;
`;

const StepperRow = styled.div`
    display: flex;
    align-items: center;
    gap: 20px;
`;

const StepperBtn = styled.button`
    width: 48px;
    height: 48px;
    border-radius: 14px;
    background: ${({ disabled }) => disabled ? colors.neutralDark : `${colors.occasionalPurple}15`};
    border: 1.5px solid ${({ disabled }) => disabled ? 'transparent' : `${colors.occasionalPurple}40`};
    color: ${({ disabled }) => disabled ? colors.nightLight : colors.occasionalPurple};
    cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.15s;
    &:not(:disabled):active { transform: scale(0.92); }
`;

const StepperValue = styled.span`
    font-size: 20px;
    font-weight: 700;
    color: ${colors.nightDark};
    min-width: 120px;
    text-align: center;
`;

const PerPersonAmount = styled.span`
    font-size: 15px;
    font-weight: 600;
    color: ${colors.occasionalPurple};
`;

const CustomerRowsSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-bottom: 20px;
`;

const CustomerRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const CustomerLabel = styled.span`
    font-size: 13px;
    font-weight: 700;
    color: ${colors.nightDark};
`;

const MethodChips = styled.div`
    display: flex;
    gap: 8px;
    overflow-x: auto;
    padding-bottom: 2px;
    scrollbar-width: none;
    &::-webkit-scrollbar { display: none; }
`;

const MethodChip = styled.button`
    padding: 9px 16px;
    border-radius: 20px;
    font-size: 14px;
    font-weight: 600;
    white-space: nowrap;
    flex-shrink: 0;
    cursor: pointer;
    transition: all 0.15s;
    border: 1.5px solid ${({ $selected }) => $selected ? colors.occasionalPurple : colors.neutralDark};
    background: ${({ $selected }) => $selected ? colors.occasionalPurple : 'transparent'};
    color: ${({ $selected }) => $selected ? '#fff' : colors.night};
    &:active { transform: scale(0.96); }
`;

const PaymentMethodsLoading = styled.div`
    display: flex;
    justify-content: center;
    padding: 24px 0;
`;

/* ───── New Tab Sheet ───── */

const SheetForm = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
`;

const FormField = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const FormLabel = styled.label`
    font-size: 13px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const FormInput = styled.input`
    border: 1.5px solid ${colors.neutralDark};
    border-radius: 10px;
    padding: 14px 16px;
    font-size: 16px;
    outline: none;
    color: ${colors.nightDark};
    background: #fff;
    width: 100%;
    box-sizing: border-box;
    transition: border-color 0.15s;
    &:focus { border-color: ${colors.occasionalPurple}; }
    &::placeholder { color: ${colors.nightLight}; }
`;

const CreateBtn = styled.button`
    padding: 15px;
    border-radius: 10px;
    background: ${({ disabled }) => disabled ? colors.neutralDark : colors.occasionalPurple};
    color: ${({ disabled }) => disabled ? colors.nightLight : '#fff'};
    font-size: 16px;
    font-weight: 700;
    border: none;
    cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
    margin-top: 4px;
    transition: opacity 0.15s;
    &:hover:not(:disabled) { opacity: 0.9; }
`;

/* ───── Select Mode ───── */

const SelectCheckbox = styled.div`
    width: 24px;
    height: 24px;
    border-radius: 7px;
    border: 2px solid ${({ $checked }) => $checked ? colors.occasionalPurple : colors.nightLight};
    background: ${({ $checked }) => $checked ? colors.occasionalPurple : 'transparent'};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: all 0.15s;
`;

/* ───── Floating Batch Bar ───── */

const FloatingBar = styled.div`
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background: #fff;
    border-top: 1px solid ${colors.neutralDark};
    box-shadow: 0 -4px 20px rgba(0,0,0,0.1);
    padding: 14px 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    z-index: 20;
    animation: ${fadeSlideIn} 0.2s ease;
`;

const FloatingBarInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const FloatingBarCount = styled.span`
    font-size: 13px;
    font-weight: 500;
    color: ${colors.nightLight};
`;

const FloatingBarTotal = styled.span`
    font-size: 20px;
    font-weight: 700;
    color: ${colors.occasionalPurple};
`;

const FloatingBarBtn = styled.button`
    padding: 14px 24px;
    border-radius: 10px;
    background: ${colors.occasionalPurple};
    color: #fff;
    font-size: 15px;
    font-weight: 700;
    border: none;
    cursor: pointer;
    white-space: nowrap;
    flex-shrink: 0;
    transition: all 0.15s;
    box-shadow: 0 2px 12px ${colors.occasionalPurple}40;
    &:hover { opacity: 0.9; }
    &:active { transform: scale(0.97); }
`;

/* ───── Batch Close Sheet ───── */

const BatchSummaryList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-bottom: 16px;
`;

const BatchDetailCard = styled.div`
    background: #fff;
    border-radius: 12px;
    border: 1.5px solid ${({ $expanded }) => $expanded ? colors.occasionalPurple : colors.neutralDark};
    overflow: hidden;
    transition: border-color 0.15s;
`;

const BatchDetailCardHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    cursor: pointer;
    transition: background 0.15s;
    &:hover { background: #f9f9fb; }
    &:active { background: #f5f5f7; }
`;

const BatchDetailCardInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1;
    min-width: 0;
`;

const BatchDetailCardTitle = styled.span`
    font-size: 15px;
    font-weight: 700;
    color: ${colors.nightDark};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const BatchDetailCardSub = styled.span`
    font-size: 13px;
    color: ${colors.nightLight};
`;

const BatchDetailCardRight = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
`;

const BatchDetailChevron = styled.div`
    transition: transform 0.2s;
    transform: ${({ $expanded }) => $expanded ? 'rotate(180deg)' : 'rotate(0)'};
    display: flex;
    align-items: center;
`;

const BatchDetailCardBody = styled.div`
    padding: 0 16px 14px;
    border-top: 1px solid ${colors.neutralDark};
`;

const BatchSummaryValue = styled.span`
    font-size: 14px;
    font-weight: 700;
    color: ${colors.occasionalPurple};
    flex-shrink: 0;
`;

const BatchProgressWrap = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 16px;
`;

const BatchProgressText = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: ${colors.night};
    text-align: center;
`;

const BatchProgressBar = styled.div`
    width: 100%;
    height: 8px;
    background: ${colors.neutralDark};
    border-radius: 4px;
    overflow: hidden;
`;

const BatchProgressFill = styled.div`
    height: 100%;
    background: ${colors.occasionalPurple};
    border-radius: 4px;
    transition: width 0.3s ease;
`;

const BatchErrorWrap = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 12px 14px;
    background: ${colors.danger}10;
    border: 1px solid ${colors.danger}30;
    border-radius: 10px;
    margin-bottom: 16px;
`;

const BatchErrorText = styled.span`
    font-size: 13px;
    font-weight: 500;
    color: ${colors.danger};
    line-height: 1.4;
`;

/* ───── Tip Toggle ───── */

const TipToggleRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    background: #f5f5f7;
    border-radius: 12px;
    margin-bottom: 16px;
    cursor: ${({ $disabled }) => $disabled ? 'default' : 'pointer'};
    opacity: ${({ $disabled }) => $disabled ? 0.45 : 1};
    transition: background 0.15s, opacity 0.15s;
    ${({ $disabled }) => !$disabled && `
        &:hover { background: #ededf0; }
        &:active { background: #e5e5e8; }
    `}
`;

const TipToggleLabel = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const TipToggleTitle = styled.span`
    font-size: 15px;
    font-weight: 700;
    color: ${colors.nightDark};
`;

const TipToggleHint = styled.span`
    font-size: 13px;
    font-weight: 500;
    color: ${colors.nightLight};
`;

const TipSwitch = styled.div`
    width: 48px;
    height: 28px;
    border-radius: 14px;
    background: ${({ $active }) => $active ? colors.occasionalPurple : colors.neutralDark};
    padding: 2px;
    display: flex;
    align-items: center;
    transition: background 0.2s;
    flex-shrink: 0;
`;

const TipSwitchKnob = styled.div`
    width: 24px;
    height: 24px;
    border-radius: 12px;
    background: #fff;
    box-shadow: 0 1px 4px rgba(0,0,0,0.15);
    transform: translateX(${({ $active }) => $active ? '20px' : '0'});
    transition: transform 0.2s;
`;

const TipBadge = styled.span`
    display: inline-block;
    margin-left: 6px;
    padding: 1px 6px;
    border-radius: 6px;
    background: ${colors.occasionalPurple}20;
    color: ${colors.occasionalPurple};
    font-size: 11px;
    font-weight: 700;
`;

const OrdersSheetBody = styled.div`
    min-height: 55vh;
    display: flex;
    flex-direction: column;
`;

const OrdersSheetHeader = styled.div`
    display: flex;
    justify-content: flex-end;
    padding: 0 20px 12px;
`;

const OrdersRefreshBtn = styled.button`
    font-size: 13px;
    font-weight: 600;
    color: ${colors.occasionalPurple};
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px 0;
    display: flex;
    align-items: center;
    gap: 6px;
    opacity: ${({ disabled }) => disabled ? 0.5 : 1};
`;

const OrdersList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 0 20px 20px;
`;

const OrderRow = styled.div`
    background: #fff;
    border: 1px solid ${colors.neutralDark};
    border-radius: 12px;
    padding: 12px 14px;
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const OrderRowHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;
`;

const OrderStatusBadge = styled.span`
    font-size: 11px;
    font-weight: 700;
    color: ${({ $color }) => $color};
    background: ${({ $color }) => $color}18;
    border: 1px solid ${({ $color }) => $color}40;
    border-radius: 20px;
    padding: 3px 10px;
`;

const OrderStation = styled.span`
    font-size: 12px;
    color: ${colors.nightLight};
    font-weight: 500;
`;

const OrderItem = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const OrderItemImage = styled.img`
    width: 40px;
    height: 40px;
    border-radius: 8px;
    object-fit: cover;
    flex-shrink: 0;
    background: ${colors.neutralDark};
`;

const OrderItemInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    font-size: 14px;
    color: ${colors.nightDark};
    font-weight: 500;
`;

const OrderItemOptions = styled.span`
    font-size: 12px;
    color: ${colors.nightLight};
    font-weight: 400;
`;

/* ───── New Tab Multi-step (tickets) ───── */

const NewTabEventSection = styled.div`
    margin-bottom: 16px;
`;

const NewTabEventTitle = styled.div`
    font-size: 13px;
    font-weight: 700;
    color: ${colors.nightDark};
    padding-bottom: 8px;
    border-bottom: 1px solid ${colors.neutralDark};
    margin-bottom: 4px;
`;

const NewTabEventDate = styled.span`
    font-weight: 400;
    color: ${colors.nightLight};
`;

const NewTabBatchRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 0;
    border-bottom: 1px solid ${colors.neutralDark}60;
`;

const NewTabBatchInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const NewTabBatchName = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const NewTabBatchMeta = styled.span`
    font-size: 12px;
    color: ${colors.nightLight};
`;

const NewTabBatchRight = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const NewTabBatchQty = styled.span`
    font-size: 15px;
    font-weight: 700;
    color: ${colors.occasionalPurple};
    min-width: 20px;
    text-align: center;
`;

const NewTabQtyBtn = styled.button`
    width: 28px;
    height: 28px;
    border-radius: 50%;
    border: 1.5px solid ${colors.neutralDark};
    background: transparent;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
    opacity: ${({ disabled }) => disabled ? 0.4 : 1};
    transition: background 0.15s;
    &:hover:not(:disabled) { background: ${colors.neutralDark}; }
`;

const NewTabBatchBtn = styled.button`
    padding: 6px 14px;
    border-radius: 20px;
    border: 1.5px solid ${colors.occasionalPurple};
    background: transparent;
    color: ${colors.occasionalPurple};
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    transition: background 0.15s;
    &:hover { background: ${colors.occasionalPurple}0A; }
`;

const NewTabActions = styled.div`
    display: flex;
    gap: 10px;
    margin-top: 8px;
`;

const SkipBtn = styled.button`
    flex: 1;
    padding: 15px;
    border-radius: 12px;
    background: transparent;
    border: 1.5px solid ${colors.neutralDark};
    color: ${colors.nightLight};
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s;
    &:hover { background: ${colors.neutralDark}; }
    &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const TicketErrorBanner = styled.div`
    margin: 10px 20px 0;
    padding: 12px 16px;
    background: ${colors.danger}0A;
    border: 1px solid ${colors.danger}30;
    border-radius: 10px;
    font-size: 13px;
    color: ${colors.danger};
    font-weight: 600;
`;
